'use client';

import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

/**
 * Inline placeholder rendered by host-coupled blocks when their
 * adapter context is null (no Provider mounted) or when the adapter
 * throws "not implemented". Matches the {@code soft_fail_boot}
 * memory — pages don't crash, operators see exactly which block
 * needs backend wiring.
 *
 * <p>Used by every host-coupled block as the
 * "adapter-missing / pending-backend" branch of its render.
 */
export function AdapterPlaceholder({
  blockType,
  adapterName,
  reason,
  children,
}: {
  /** Block type id, e.g. {@code BlogArticleGrid}. */
  blockType: string;
  /** Adapter the block needs, e.g. {@code BlogSource}. */
  adapterName: string;
  /** Short label shown alongside the block name. Defaults to the
   *  catalogue's "adapter pending" wording; pass a more specific
   *  message when known (e.g. {@code Backend pending}). */
  reason?: string;
  /** Optional override content. When provided, replaces the default
   *  placeholder card entirely. */
  children?: ReactNode;
}) {
  const { t } = useTranslation('site-builder-blocks');
  if (children) return <>{children}</>;
  return (
    <div
      role="status"
      style={{
        margin: 'var(--space-4, 16px) 0',
        padding: 'var(--space-4, 16px) var(--space-5, 20px)',
        borderRadius: 12,
        border: '1px dashed var(--color-border-subtle, rgba(0, 0, 0, 0.12))',
        background: 'var(--color-surface-muted, rgba(127, 127, 127, 0.04))',
        color: 'var(--color-text-secondary, #6b7280)',
        fontSize: '13px',
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'var(--color-text-tertiary, #9ca3af)',
          flexShrink: 0,
        }}
      />
      <div>
        <div style={{ fontWeight: 600, color: 'var(--color-text-primary, #111827)' }}>
          {blockType}
        </div>
        <div style={{ marginTop: 2 }}>
          <Trans
            t={t}
            i18nKey="shared.adapter.requires"
            values={{ reason: reason ?? t('shared.adapter.reason'), adapterName }}
            components={{ c: <code /> }}
          />
        </div>
      </div>
    </div>
  );
}
